import React, { useCallback, useEffect, useMemo, useState } from "react";
import GenericModal from "./GenericModal";
import useModalStore from "../../stores/modalStore";
import useUserStore from "../../stores/userStore";
import { fetchEmployeeAppointmentsForDate } from "../../services/daysmartService";
import { LOCATION_ID_TO_NAME } from "../../utils/constants";
import {
    AlertCircle, Calendar, CalendarClock, ChevronLeft, ChevronRight,
    Clock, MapPin, PawPrint
} from "lucide-react";

const SALON_TZ = "America/Chicago";
const DAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const MAX_FUTURE_DAYS = 3;

const toISODate = (date) => date.toLocaleDateString("en-CA", { timeZone: SALON_TZ });

const getTodayCentral = () => toISODate(new Date());

const parseISODate = (isoDate) => new Date(`${isoDate}T12:00:00`);

const addDays = (isoDate, offset) => {
    const d = parseISODate(isoDate);
    d.setDate(d.getDate() + offset);
    return toISODate(d);
};

const normalizeEffectiveDate = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value.slice(0, 10);
    if (value instanceof Date) return toISODate(value);
    return String(value).slice(0, 10);
};

const getApplicableSchedule = (scheduleRecords, isoDate) => {
    if (!Array.isArray(scheduleRecords) || scheduleRecords.length === 0) return null;
    const applicable = scheduleRecords
        .filter((s) => normalizeEffectiveDate(s?.effective_date) && normalizeEffectiveDate(s.effective_date) <= isoDate)
        .sort((a, b) => normalizeEffectiveDate(b.effective_date).localeCompare(normalizeEffectiveDate(a.effective_date)));
    return applicable[0] || null;
};

const getScheduledHoursForDate = (scheduleRecords, isoDate) => {
    const schedule = getApplicableSchedule(scheduleRecords, isoDate);
    if (!schedule) return 0;
    const dayKey = DAY_KEYS[parseISODate(isoDate).getDay()];
    return parseFloat(schedule[dayKey]) || 0;
};

const getMinAllowedDate = () => getTodayCentral();

const getMaxAllowedDate = () => addDays(getTodayCentral(), MAX_FUTURE_DAYS);

const clampToAllowedRange = (isoDate) => {
    const min = getMinAllowedDate();
    const max = getMaxAllowedDate();
    if (!isoDate || isoDate < min) return min;
    if (isoDate > max) return max;
    return isoDate;
};

const isDateAllowed = (isoDate) => {
    if (!isoDate) return false;
    return isoDate >= getMinAllowedDate() && isoDate <= getMaxAllowedDate();
};

export const findNextScheduledWorkDay = (scheduleRecords, fromIsoDate = getTodayCentral(), maxDays = MAX_FUTURE_DAYS + 1) => {
    const start = clampToAllowedRange(fromIsoDate);
    const maxSearchDays = Math.min(maxDays, MAX_FUTURE_DAYS + 1);
    for (let i = 0; i < maxSearchDays; i++) {
        const iso = addDays(start, i);
        if (!isDateAllowed(iso)) break;
        if (getScheduledHoursForDate(scheduleRecords, iso) > 0) {
            return iso;
        }
    }
    return start;
};

const formatDisplayDate = (isoDate) => {
    if (!isoDate) return "";
    return parseISODate(isoDate).toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
    });
};

const formatTime = (isoString) => {
    if (!isoString) return "—";
    return new Date(isoString).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
};

const getLocationName = (locId) => LOCATION_ID_TO_NAME[locId] || (locId === 1 ? "Hopkins" : locId === 2 ? "Inver Grove" : "");

const isCancelled = (status) => (status || "").toLowerCase().includes("cancel");

const groupAppointments = (appointments) => {
    const groups = new Map();
    (appointments || []).forEach((appt) => {
        if (isCancelled(appt.status)) return;
        const key = appt.appointment_id || `${appt.client_id}-${appt.start_time}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(appt);
    });

    return Array.from(groups.values())
        .map((items) => {
            items.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
            const startTimes = items.map((i) => i.start_time).filter(Boolean);
            const endTimes = items.map((i) => i.end_time).filter(Boolean);
            return {
                key: items[0].appointment_id || items[0].start_time,
                client_name: items[0].client_name || "Unknown Client",
                pets: [...new Set(items.map((i) => i.pet_name).filter(Boolean))],
                services: [...new Set(items.map((i) => i.service_name).filter(Boolean))],
                start_time: startTimes.sort((a, b) => new Date(a) - new Date(b))[0],
                end_time: endTimes.sort((a, b) => new Date(b) - new Date(a))[0],
                status: items[0].status,
                location_id: items[0].location_id,
                notes: items.map((i) => i.notes).find(Boolean),
            };
        })
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
};

const getStatusClasses = (status) => {
    const s = (status || "").toLowerCase();
    if (s.includes("confirm")) return "bg-green-100 text-green-800 border-green-200";
    if (s.includes("check") && s.includes("in")) return "bg-blue-100 text-blue-800 border-blue-200";
    if (s.includes("complete") || s.includes("closed")) return "bg-gray-100 text-gray-600 border-gray-200";
    if (s.includes("no show")) return "bg-orange-100 text-orange-800 border-orange-200";
    return "bg-purple-100 text-purple-800 border-purple-200";
};

const SeeScheduleModal = () => {
    const show = useModalStore((state) => state.showSeeScheduleModal);
    const { currentUser, employees } = useUserStore();

    const employee = useMemo(
        () => (employees || []).find((emp) => Number(emp.user_id) === Number(currentUser?.user_id)),
        [employees, currentUser?.user_id]
    );

    const scheduleRecords = employee?.schedule_record;
    const daysmartMap = employee?.location_to_daysmart_id_map;
    const hasDaysmartIds = Object.values(daysmartMap || {}).some((id) => id);

    const nextWorkDay = useMemo(
        () => findNextScheduledWorkDay(scheduleRecords),
        [scheduleRecords]
    );

    const [selectedDate, setSelectedDate] = useState(nextWorkDay);
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (show) {
            setSelectedDate(clampToAllowedRange(findNextScheduledWorkDay(scheduleRecords)));
            setAppointments([]);
            setError(null);
        }
    }, [show, scheduleRecords]);

    useEffect(() => {
        if (!show) return;
        const clamped = clampToAllowedRange(selectedDate);
        if (clamped !== selectedDate) {
            setSelectedDate(clamped);
        }
    }, [show, selectedDate]);

    const loadAppointments = useCallback(async (isoDate) => {
        if (!isDateAllowed(isoDate)) {
            setAppointments([]);
            setError(null);
            return;
        }

        if (!hasDaysmartIds) {
            setAppointments([]);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const result = await fetchEmployeeAppointmentsForDate(parseISODate(isoDate), daysmartMap || {}, true);
            setAppointments(result.appointments || []);
        } catch (err) {
            console.error("Failed to load schedule:", err);
            setError(err.message || "Failed to load appointments");
            setAppointments([]);
        } finally {
            setLoading(false);
        }
    }, [daysmartMap, hasDaysmartIds]);

    useEffect(() => {
        if (show && selectedDate) {
            loadAppointments(selectedDate);
        }
    }, [show, selectedDate, loadAppointments]);

    const grouped = useMemo(() => groupAppointments(appointments), [appointments]);
    const scheduledHours = getScheduledHoursForDate(scheduleRecords, selectedDate);
    const todayCentral = getTodayCentral();
    const maxAllowedDate = getMaxAllowedDate();
    const isToday = selectedDate === todayCentral;
    const isNextWorkDay = selectedDate === nextWorkDay;
    const canGoPrev = selectedDate > todayCentral;
    const canGoNext = selectedDate < maxAllowedDate;

    const firstAppt = grouped[0];
    const lastAppt = grouped[grouped.length - 1];
    const petCount = grouped.reduce((sum, g) => sum + (g.pets.length || 1), 0);

    return (
        <GenericModal
            name="seeScheduleModal"
            title="Your Schedule"
            hideButton={true}
            modalMaxWidth="max-w-2xl"
        >
            <div className="p-6 space-y-5">
                <div className="flex items-center justify-center gap-3 -mt-2">
                    <button
                        onClick={() => setSelectedDate((d) => clampToAllowedRange(addDays(d, -1)))}
                        disabled={loading || !canGoPrev}
                        className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-100 hover:border-gray-300 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:border-gray-200 disabled:cursor-not-allowed"
                        title={canGoPrev ? "Previous day" : "Can't view past days"}
                    >
                        <ChevronLeft size={18} />
                    </button>

                    <div className="flex flex-col items-center min-w-[240px]">
                        <div className="flex items-center gap-2 justify-center">
                            {isToday && (
                                <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 rounded-full">
                                    Today
                                </span>
                            )}
                            {!isToday && isNextWorkDay && (
                                <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-indigo-100 text-indigo-700 rounded-full">
                                    Next Work Day
                                </span>
                            )}
                            <span className="text-sm font-medium text-gray-700">{formatDisplayDate(selectedDate)}</span>
                        </div>
                        {scheduledHours > 0 && (
                            <span className="text-xs text-gray-400 mt-0.5">
                                Scheduled {scheduledHours}h
                            </span>
                        )}
                    </div>

                    <button
                        onClick={() => setSelectedDate((d) => clampToAllowedRange(addDays(d, 1)))}
                        disabled={loading || !canGoNext}
                        className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-100 hover:border-gray-300 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:border-gray-200 disabled:cursor-not-allowed"
                        title={canGoNext ? "Next day" : "Can't view more than 3 days ahead"}
                    >
                        <ChevronRight size={18} />
                    </button>

                    {!isNextWorkDay && (
                        <button
                            onClick={() => setSelectedDate(clampToAllowedRange(nextWorkDay))}
                            disabled={loading}
                            className="ml-1 px-2.5 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors disabled:opacity-40"
                            title="Go to next work day"
                        >
                            <Calendar size={12} className="inline mr-1" />
                            {nextWorkDay === todayCentral ? "Today" : "Next Work Day"}
                        </button>
                    )}
                </div>

                {loading && (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                        <span className="ml-3 text-gray-500">Loading appointments…</span>
                    </div>
                )}

                {error && (
                    <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-lg px-4 py-3">
                        <AlertCircle size={18} />
                        <span>{error}</span>
                    </div>
                )}

                {!loading && !error && !hasDaysmartIds && (
                    <div className="text-center py-10 px-4">
                        <CalendarClock size={36} className="mx-auto text-gray-300 mb-3" />
                        <p className="text-sm font-medium text-gray-600">No DaySmart profile linked</p>
                        <p className="text-xs text-gray-400 mt-1">
                            Appointments can’t be loaded until a DaySmart ID is set for your account.
                        </p>
                        {scheduledHours > 0 && (
                            <p className="text-xs text-gray-500 mt-3">
                                You’re scheduled for {scheduledHours} hours on {formatDisplayDate(selectedDate)}.
                            </p>
                        )}
                    </div>
                )}

                {!loading && !error && hasDaysmartIds && grouped.length === 0 && (
                    <div className="text-center py-10 px-4">
                        <CalendarClock size={36} className="mx-auto text-gray-300 mb-3" />
                        <p className="text-sm font-medium text-gray-600">No appointments booked</p>
                        <p className="text-xs text-gray-400 mt-1">
                            {scheduledHours > 0
                                ? `You’re scheduled to work ${scheduledHours} hours, but nothing is on the book yet.`
                                : "Nothing is on the book for this day."}
                        </p>
                    </div>
                )}

                {!loading && !error && grouped.length > 0 && (
                    <>
                        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl p-4 text-white">
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <div className="text-2xl font-bold">{grouped.length}</div>
                                    <div className="text-xs opacity-80">Appointments</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold">{petCount}</div>
                                    <div className="text-xs opacity-80">Pets</div>
                                </div>
                                <div>
                                    <div className="text-lg font-bold leading-7">
                                        {formatTime(firstAppt.start_time)}
                                        <span className="text-xs font-normal opacity-70"> – {formatTime(lastAppt.end_time)}</span>
                                    </div>
                                    <div className="text-xs opacity-80">First to last</div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            {grouped.map((appt) => (
                                <div
                                    key={appt.key}
                                    className="border border-gray-200 rounded-xl px-4 py-3 hover:border-indigo-200 hover:bg-indigo-50/30 transition-colors"
                                >
                                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-1.5 text-sm font-semibold text-indigo-700">
                                                <Clock size={14} />
                                                {formatTime(appt.start_time)}
                                                {appt.end_time && (
                                                    <span className="text-gray-400 font-normal">– {formatTime(appt.end_time)}</span>
                                                )}
                                            </div>
                                            <div className="font-medium text-gray-800 mt-1">{appt.client_name}</div>
                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-gray-500 mt-0.5">
                                                {appt.pets.length > 0 && (
                                                    <span className="inline-flex items-center gap-1">
                                                        <PawPrint size={12} />
                                                        {appt.pets.join(", ")}
                                                    </span>
                                                )}
                                                {appt.services.length > 0 && (
                                                    <span>{appt.services.join(" · ")}</span>
                                                )}
                                            </div>
                                            {appt.notes && (
                                                <p className="text-xs text-gray-400 mt-1 italic">{appt.notes}</p>
                                            )}
                                        </div>
                                        <div className="flex sm:flex-col items-center sm:items-end gap-2 sm:gap-1 shrink-0">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border ${getStatusClasses(appt.status)}`}>
                                                {appt.status || "Scheduled"}
                                            </span>
                                            {appt.location_id && (
                                                <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
                                                    <MapPin size={10} />
                                                    {getLocationName(appt.location_id)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </GenericModal>
    );
};

export default SeeScheduleModal;
